import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DeviceMobile, DownloadSimple, Copy, Check, WhatsappLogo, ArrowRight } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { EDITION } from "@/lib/edition";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// On-demand install popup: QR code + clickable short URL (linked to the /install page).
// Shown on both editions and all devices.
export default function InstallPopup({ open, onOpenChange, t = (s) => s }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [canInstall, setCanInstall] = useState(!!window.deferredInstallPrompt);
  const brand = EDITION.brand;

  useEffect(() => {
    if (!open || data) return;
    api.get(`/app/install?edition=${EDITION.id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(false));
  }, [open, data]);

  useEffect(() => {
    const onInstallable = () => setCanInstall(true);
    window.addEventListener("pwa-installable", onInstallable);
    return () => window.removeEventListener("pwa-installable", onInstallable);
  }, []);

  const shortUrl = data && data !== false ? data.short_url : "";
  const qrSrc = data && data !== false
    ? (data.qr_png?.startsWith("http") ? data.qr_png : `${BACKEND}${data.qr_png}`)
    : null;

  const copy = async () => {
    try { await navigator.clipboard.writeText(shortUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error(t("Couldn't copy")); }
  };

  const install = async () => {
    const dp = window.deferredInstallPrompt;
    if (!dp) { toast.message(t("Use your browser menu → \u201cInstall app\u201d / \u201cAdd to Home screen\u201d.")); return; }
    dp.prompt();
    try { await dp.userChoice; } catch { /* ignore */ }
    window.deferredInstallPrompt = null;
    setCanInstall(false);
  };

  const waText = encodeURIComponent(`${t("Install the")} ${brand} ${t("app on your phone — scan or tap:")} ${shortUrl}`);
  const waHref = `https://wa.me/?text=${waText}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="install-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground"><DeviceMobile size={18} weight="fill" /></span>
            {t("Get the")} {brand} {t("app")}
          </DialogTitle>
          <DialogDescription>{t("Scan the code or tap the link to install the app on your phone.")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center">
          <div className="grid h-52 w-52 place-items-center rounded-2xl border border-border/60 bg-white p-3">
            {data === null ? <div className="h-full w-full animate-pulse rounded-xl bg-muted" data-testid="install-popup-qr-loading" />
              : qrSrc ? <img src={qrSrc} alt="Install QR code" className="h-full w-full object-contain" data-testid="install-popup-qr" />
              : <span className="text-sm text-muted-foreground">{t("QR unavailable")}</span>}
          </div>

          {shortUrl && (
            <div className="mt-4 flex w-full items-center gap-2 rounded-full border border-border/60 bg-muted/40 py-1.5 pl-4 pr-1.5">
              <a href={shortUrl} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-left text-sm font-medium text-primary hover:underline" data-testid="install-popup-short-url">{shortUrl}</a>
              <Button size="sm" variant="secondary" className="shrink-0 gap-1 rounded-full" onClick={copy} data-testid="install-popup-copy">
                {copied ? <><Check size={14} /> {t("Copied")}</> : <><Copy size={14} /> {t("Copy")}</>}
              </Button>
            </div>
          )}

          <div className="mt-3 grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
            <Button onClick={install} className="w-full gap-2 rounded-full" data-testid="install-popup-install-btn">
              <DownloadSimple size={16} weight="bold" /> {canInstall ? t("Install app") : t("Add to Home screen")}
            </Button>
            <a href={waHref} target="_blank" rel="noreferrer" data-testid="install-popup-whatsapp">
              <Button className="w-full gap-2 rounded-full bg-[#25D366] text-white hover:bg-[#1eb257]"><WhatsappLogo size={16} weight="fill" /> {t("Send to my phone")}</Button>
            </a>
          </div>

          <Link to="/install" onClick={() => onOpenChange(false)} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-secondary hover:underline" data-testid="install-popup-more">
            {t("More install options")} <ArrowRight size={14} />
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
